import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, HelpCircle, Minus, Search, X } from 'lucide-react';
import { rbacApi } from '../../services/api';

/**
 * "Can this principal do X, and which gate decided?"
 *
 * Reading raw grant rows to work this out is guesswork: a grant is only one of
 * five gates, and the two that most often refuse are the two that report least
 * — a denied list comes back as `total: 0`, indistinguishable from empty.
 *
 * Everything here comes from the server's resolver. Re-deriving access in the
 * browser would give a second, quietly divergent answer to the one question you
 * consult this page to settle.
 */

const PERMISSIONS = [
  'workitem:view', 'workitem:create', 'workitem:edit', 'workitem:delete',
  'project:view', 'idea:create', 'comment:create', 'timeentry:create',
];

const DECISION_STYLE: Record<string, { icon: typeof Check; cls: string; label: string }> = {
  pass: { icon: Check, cls: 'text-emerald-600 dark:text-emerald-400', label: 'pass' },
  deny: { icon: X, cls: 'text-red-600 dark:text-red-400', label: 'DENY' },
  unknown: { icon: HelpCircle, cls: 'text-amber-600 dark:text-amber-400', label: 'not evaluated' },
  'n/a': { icon: Minus, cls: 'text-gray-400 dark:text-gray-500', label: 'n/a' },
};

type Props = { users: any[]; tokens: any[]; projects: any[] };

export default function AccessExplorer({ users, tokens, projects }: Props) {
  const [kind, setKind] = useState<'token' | 'user'>('token');
  const [subjectId, setSubjectId] = useState('');
  const [resolved, setResolved] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [warnings, setWarnings] = useState<any[]>([]);

  const [trace, setTrace] = useState<any>(null);
  const [probe, setProbe] = useState({ permission: 'workitem:edit', projectId: '', workItemType: '' });

  const subjects = kind === 'token' ? tokens : users;
  const label = (s: any) => (kind === 'token' ? s.name : `${s.name || s.email} (${s.email})`);

  useEffect(() => { rbacApi.accessWarnings().then((r) => setWarnings(r.items || [])).catch(() => {}); }, []);
  useEffect(() => { setSubjectId(''); setResolved(null); setTrace(null); }, [kind]);

  const load = useCallback(async (id: string) => {
    if (!id) { setResolved(null); return; }
    setLoading(true);
    try {
      setResolved(kind === 'token'
        ? await rbacApi.effectiveToken(id)
        : await rbacApi.effectiveUserResolved(id));
    } catch { setResolved(null); } finally { setLoading(false); }
  }, [kind]);

  useEffect(() => { load(subjectId); }, [subjectId, load]);

  const runTrace = async () => {
    if (!subjectId || !probe.projectId) return;
    try {
      setTrace(await rbacApi.explain({
        subjectKind: kind, subjectId, permission: probe.permission,
        projectId: probe.projectId, workItemType: probe.workItemType || undefined,
      }));
    } catch { setTrace(null); }
  };

  const myWarnings = useMemo(
    () => warnings.filter((w) => w.subjectId === subjectId),
    [warnings, subjectId],
  );

  const sel = 'px-2 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md';

  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        A request passes five independent gates. This asks the server which one decides —
        a grant is only the last of them.
      </p>

      {warnings.length > 0 && (
        <div className="mb-3 rounded-lg border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 p-3">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-300">
            <AlertTriangle size={14} />
            {warnings.length} configuration{warnings.length > 1 ? 's' : ''} that {warnings.length > 1 ? 'do' : 'does'} not mean what the assignments imply
          </div>
          <div className="mt-2 grid gap-1.5">
            {warnings.map((w) => (
              <button
                key={w.subjectId}
                onClick={() => { setKind(w.subjectKind === 'user' ? 'user' : 'token'); setSubjectId(w.subjectId); }}
                className="text-left text-xs text-amber-900 dark:text-amber-200 hover:underline"
              >
                <span className="font-medium">{w.name}</span>
                <span className="opacity-70"> ({w.subjectKind})</span> — {w.warnings[0]}
                {w.warnings.length > 1 && <span className="opacity-70"> +{w.warnings.length - 1} more</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select className={sel} value={kind} onChange={(e) => setKind(e.target.value as any)}>
          <option value="token">API token</option>
          <option value="user">User</option>
        </select>
        <select className={`${sel} min-w-[16rem]`} value={subjectId} onChange={(e) => setSubjectId(e.target.value)}>
          <option value="">— pick a {kind === 'token' ? 'token' : 'user'} —</option>
          {subjects.map((s: any) => <option key={s._id} value={s._id}>{label(s)}</option>)}
        </select>
        {loading && <span className="text-xs text-gray-400">resolving…</span>}
      </div>

      {resolved && (
        <>
          <div className="grid gap-2 mb-4">
            {myWarnings.flatMap((w) => w.warnings).map((text: string, i: number) => (
              <div key={i} className="flex gap-2 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-md px-2.5 py-2">
                <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                <span>{text}</span>
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <Panel title="Configuration">
              {kind === 'token' ? (
                <>
                  <KV k="Account type" v={resolved.accountType} />
                  <KV k="Project floor" v={resolved.floor?.unrestricted
                    ? `unrestricted (${resolved.floor.kind})`
                    : `${resolved.floor?.projectIds?.length || 0} project(s)`} />
                  <KV k="Permissions" v={(resolved.declaredPermissions || []).join(', ') || '—'} />
                  <KV k="Allowed types" v={(resolved.allowedTypes || []).join(', ') || 'all'} />
                  <KV k="Route allow-list" v={(resolved.allowedRoutes || []).join(', ') || 'unrestricted'} />
                  {resolved.unmappedPermissions?.length > 0 && (
                    <KV k="Maps to nothing" danger v={resolved.unmappedPermissions.join(', ')} />
                  )}
                </>
              ) : (
                <>
                  <KV k="Role" v={resolved.role} />
                  <KV k="Role scope" v={resolved.roleScope} />
                  <KV k="Assigned projects" v={String(resolved.assignedProjects?.length || 0)} />
                </>
              )}
              <KV k="Grants" v={String(resolved.grantCount ?? 0)} />
            </Panel>

            <Panel title={`Reaches ${resolved.projects?.length || 0} project(s)`}>
              <div className="max-h-56 overflow-y-auto pr-1">
                {(resolved.projects || []).map((p: any) => (
                  <div key={p.projectId} className="flex items-baseline gap-2 py-0.5">
                    <span className="text-xs text-gray-700 dark:text-gray-300 truncate flex-1">{p.name}</span>
                    <span className={`text-[11px] ${p.granted?.length ? 'text-gray-500 dark:text-gray-400' : 'text-red-600 dark:text-red-400'}`}>
                      {p.granted?.length ? p.granted.map((g: string) => g.split(':')[1]).join(' · ') : 'no grant — reads as empty'}
                    </span>
                  </div>
                ))}
                {!resolved.projects?.length && <div className="text-xs text-gray-400 py-3">No projects reachable.</div>}
              </div>
            </Panel>
          </div>

          <Panel title="Explain a decision">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <select className={sel} value={probe.permission} onChange={(e) => setProbe({ ...probe, permission: e.target.value })}>
                {PERMISSIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <span className="text-xs text-gray-400">on</span>
              <select className={`${sel} min-w-[12rem]`} value={probe.projectId} onChange={(e) => setProbe({ ...probe, projectId: e.target.value })}>
                <option value="">— project —</option>
                {projects.map((p: any) => <option key={p._id} value={p._id}>{p.name}</option>)}
              </select>
              <input className={`${sel} w-36`} placeholder="type (optional)" value={probe.workItemType}
                     onChange={(e) => setProbe({ ...probe, workItemType: e.target.value })} />
              <button onClick={runTrace} disabled={!probe.projectId}
                      className="text-sm flex items-center gap-1 px-3 py-1.5 bg-accent text-white rounded-md disabled:opacity-40">
                <Search size={14} /> Trace
              </button>
            </div>

            {trace && (
              <div>
                <div className={`text-sm font-medium mb-2 ${trace.allowed ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                  {trace.allowed ? 'Allowed' : `Denied at: ${trace.decidedBy}`}
                  {trace.undetermined?.length > 0 && (
                    <span className="ml-2 text-[11px] font-normal text-amber-600 dark:text-amber-400">
                      {trace.undetermined.join(', ')} not evaluated — supply the call details
                    </span>
                  )}
                </div>
                <div className="grid gap-1">
                  {trace.trace.map((step: any, i: number) => {
                    const s = DECISION_STYLE[step.decision] || DECISION_STYLE['n/a'];
                    const Icon = s.icon;
                    return (
                      <div key={i} className={`flex gap-2 text-xs py-1.5 px-2 rounded ${step.gate === trace.decidedBy ? 'bg-red-50 dark:bg-red-900/20' : ''}`}>
                        <span className="text-gray-400 tabular-nums">{i + 1}</span>
                        <Icon size={13} className={`shrink-0 mt-0.5 ${s.cls}`} />
                        <span className="w-56 shrink-0 text-gray-700 dark:text-gray-300">{step.label}</span>
                        <span className="text-gray-500 dark:text-gray-400">{step.detail}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Panel>
        </>
      )}

      {!resolved && !loading && (
        <div className="text-sm text-gray-400 py-6 text-center">
          Pick a principal to see what it can actually reach.
        </div>
      )}
    </div>
  );
}

function Panel({ title, children }: any) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
      <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">{title}</div>
      {children}
    </div>
  );
}

function KV({ k, v, danger }: { k: string; v: string; danger?: boolean }) {
  return (
    <div className="flex gap-2 py-0.5 text-xs">
      <span className="w-32 shrink-0 text-gray-500 dark:text-gray-400">{k}</span>
      <span className={danger ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-800 dark:text-gray-200'}>{v}</span>
    </div>
  );
}
